// Essai + derniers envois: a sample SMS to the team's own phone (5 per day),
// then the latest SMS with their outcome (number masked).

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane } from 'react-icons/fa';
import { EVENTS, fail, formatWhen, post } from './api';

const STATUS_TONE = { sent: 'success', scheduled: 'info', failed: 'danger', skipped: 'warning', sending: 'muted' };

const statusLabel = (item, lang, t) => {
    if (item.status === 'scheduled' && item.scheduled_for) {
        return t('customerSms.history.scheduledAt', 'Programmé · {{when}}', { when: formatWhen(item.scheduled_for, lang) });
    }
    if (item.status === 'skipped' && item.error === 'platform_sender_inactive') {
        return t('customerSms.history.senderInactive', 'Non envoyé · expéditeur inactif');
    }
    return t(`customerSms.history.status.${item.status}`, item.status);
};

const ActivitySection = ({ view, messages, onSent, lang }) => {
    const { t } = useTranslation();
    const [phone, setPhone] = useState('');
    const [event, setEvent] = useState('parcel_problem');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const sendTest = async (e) => {
        e.preventDefault();
        setBusy(true);
        setResult(null);
        try {
            const res = await post('/test', { phone, event });
            setResult({ tone: 'success', text: t('customerSms.test.sent', 'SMS d’essai envoyé au {{to}} sous « {{sender}} ».', { to: res.data.to, sender: res.data.sender }) });
            onSent();
        } catch (err) {
            setResult({ tone: 'danger', text: fail(err, t('customerSms.test.error', "L'essai n'a pas pu être envoyé.")) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {view.can_edit && (
                <section className="csms-card" aria-labelledby="csms-test-title">
                    <header className="csms-card-header">
                        <div>
                            <h2 id="csms-test-title">{t('customerSms.test.title', 'Tester')}</h2>
                            <p>{t('customerSms.test.subtitle', 'Recevez une notification sur votre propre mobile, avec les textes enregistrés.')}</p>
                        </div>
                    </header>
                    <form className="csms-test" onSubmit={sendTest}>
                        <label className="csms-field">
                            <span>{t('customerSms.test.phone', 'Votre mobile')}</span>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" maxLength={32} />
                        </label>
                        <label className="csms-field">
                            <span>{t('customerSms.test.event', 'Notification')}</span>
                            <select value={event} onChange={(e) => setEvent(e.target.value)}>
                                {EVENTS.map((ev) => <option key={ev} value={ev}>{t(`customerSms.events.${ev}.title`, ev)}</option>)}
                            </select>
                        </label>
                        <button type="submit" className="u-btn u-btn--secondary" disabled={busy || phone.trim().length < 6 || !view.ovh_configured}>
                            <FaPaperPlane /> {busy ? t('customerSms.test.sending', 'Envoi…') : t('customerSms.test.send', "Envoyer l'essai")}
                        </button>
                    </form>
                    <p className="csms-note">
                        {t('customerSms.test.limit', '{{n}} essais par jour. Le numéro n’est pas conservé (seuls ses 2 derniers chiffres apparaissent ci-dessous).', { n: view.limits?.test_per_day || 5 })}
                    </p>
                    {result && <p className={`csms-inline-feedback csms-inline-feedback--${result.tone}`} role="status">{result.text}</p>}
                </section>
            )}

            <section className="csms-card" aria-labelledby="csms-history-title">
                <header className="csms-card-header">
                    <div>
                        <h2 id="csms-history-title">{t('customerSms.history.title', 'Derniers envois')}</h2>
                        <p>{t('customerSms.history.subtitle', 'Les 30 derniers SMS de votre compte, conservés 180 jours.')}</p>
                    </div>
                </header>
                {messages.length === 0 ? (
                    <p className="csms-empty">{t('customerSms.history.empty', 'Aucun SMS envoyé pour le moment.')}</p>
                ) : (
                    <div className="csms-table-wrap">
                        <table className="csms-table">
                            <thead>
                                <tr>
                                    <th>{t('customerSms.history.date', 'Date')}</th>
                                    <th>{t('customerSms.history.event', 'Notification')}</th>
                                    <th>{t('customerSms.history.parcel', 'Colis')}</th>
                                    <th>{t('customerSms.history.to', 'Destinataire')}</th>
                                    <th>{t('customerSms.history.sender', 'Expéditeur')}</th>
                                    <th>{t('customerSms.history.statusCol', 'Statut')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map((item) => (
                                    <tr key={item.id}>
                                        <td className="csms-nowrap">{formatWhen(item.created_at, lang)}</td>
                                        <td>
                                            {item.event === 'test'
                                                ? `${t('customerSms.history.test', 'Essai')} · ${t(`customerSms.events.${item.test_of}.title`, item.test_of || '')}`
                                                : t(`customerSms.events.${item.event}.title`, item.event)}
                                        </td>
                                        <td className="csms-mono">{item.tracking_number || '—'}</td>
                                        <td className="csms-mono">{item.to}</td>
                                        <td>{item.sender}</td>
                                        <td>
                                            <span className={`csms-chip csms-chip--${STATUS_TONE[item.status] || 'muted'}`}
                                                title={item.status === 'failed' ? item.error || '' : undefined}>
                                                {statusLabel(item, lang, t)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </>
    );
};

export default ActivitySection;
